import {
  Badge,
  Button,
  createListCollection,
  Flex,
  Heading,
  IconButton,
  Popover,
  Portal,
  Select,
  Tag,
  Text,
} from '@chakra-ui/react';
import ConditionTextInput from '@components/common/ConditionTextInput';
import EditDeleteActions from '@components/common/EditDeleteActions';
import type { InventoryNeed } from '@features/supply/fakeSupplyChainData';
import { formatDate } from 'helpers/dates';
import useControlledPopover from 'hooks/useControlledPopover';
import { memo, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { LuArrowLeft, LuPackagePlus, LuPlus } from 'react-icons/lu';

export interface SupplyCommitment {
  id: string;
  quantity: string;
  price: string;
  deliveryDate: string; // ISO date from the need
}

type PopoverView = 'form' | 'list';

// ─── Delivery Date Picker ────────────────────────────────────────────────────

const DeliveryDatePicker = ({
  deliveryDates,
  selectedDate,
  onSelect,
}: {
  deliveryDates: string[];
  selectedDate: string;
  onSelect: (date: string) => void;
}) => {
  const collection = useMemo(
    () =>
      createListCollection({
        items: deliveryDates.map((d) => ({ value: d, label: formatDate(d) })),
      }),
    [deliveryDates],
  );

  const onValueChange = useCallback(
    (details: { value: string[] }) => {
      if (details.value.length > 0) {
        onSelect(details.value[0]);
      }
    },
    [onSelect],
  );

  return (
    <Flex direction="row" gap={2} alignItems="center">
      <Tag.Root bg="surface.muted" borderColor="border.light" borderWidth="1px" borderRadius="xl">
        <Tag.Label>Deliver by</Tag.Label>
      </Tag.Root>
      <Select.Root
        collection={collection}
        value={[selectedDate]}
        onValueChange={onValueChange}
        size="xs"
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger borderColor="border.light" borderRadius="lg">
            <Select.ValueText placeholder="Select date" />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content bg="surface.background" borderRadius="lg">
              {collection.items.map((item) => (
                <Select.Item item={item} key={item.value}>
                  {item.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    </Flex>
  );
};

// ─── Format Options ──────────────────────────────────────────────────────────

const QUANTITY_FORMAT: Intl.NumberFormatOptions = {
  style: 'unit',
  unit: 'kilogram',
  unitDisplay: 'short',
};

const PRICE_FORMAT: Intl.NumberFormatOptions = {
  style: 'currency',
  currency: 'GHS',
  currencyDisplay: 'code',
};

// ─── Commitment Form ─────────────────────────────────────────────────────────

interface CommitmentFormProps {
  deliveryDates: string[];
  initialValues?: SupplyCommitment;
  onSave: (commitment: Omit<SupplyCommitment, 'id'>) => void;
  onCancel?: () => void;
  isEditing?: boolean;
  hideHeader?: boolean;
  hideSubmit?: boolean;
  submitRef?: React.RefObject<{ submit: () => void } | null>;
}

const CommitmentForm = memo(
  ({ deliveryDates, initialValues, onSave, onCancel, isEditing, hideHeader, hideSubmit, submitRef }: CommitmentFormProps) => {
    const [quantity, setQuantity] = useState(initialValues?.quantity ?? '');
    const [price, setPrice] = useState(initialValues?.price ?? '');
    const [deliveryDate, setDeliveryDate] = useState(
      initialValues?.deliveryDate ?? deliveryDates[0] ?? '',
    );

    const onQuantityChange = useCallback((details: { value: string }) => {
      setQuantity(details.value);
    }, []);

    const onPriceChange = useCallback((details: { value: string }) => {
      setPrice(details.value);
    }, []);

    const onDateSelect = useCallback((date: string) => {
      setDeliveryDate(date);
    }, []);

    const isValid = quantity !== '' && price !== '' && deliveryDate !== '';

    const handleSave = useCallback(() => {
      if (!isValid) {
        return;
      }
      onSave({ quantity, price, deliveryDate });
    }, [isValid, quantity, price, deliveryDate, onSave]);

    useImperativeHandle(submitRef, () => ({
      submit: handleSave,
    }), [handleSave]);

    return (
      <Flex direction="column" gap={4}>
        {!hideHeader && (
          <Flex alignItems="center" gap={2}>
            {onCancel != null && (
              <IconButton
                aria-label="Back"
                size="2xs"
                variant="ghost"
                borderRadius="md"
                onClick={onCancel}
              >
                <LuArrowLeft />
              </IconButton>
            )}
            <Heading fontSize="sm" lineHeight={1}>
              {isEditing ? 'Edit commitment' : 'New commitment'}
            </Heading>
          </Flex>
        )}
        <ConditionTextInput
          label="Quantity"
          value={quantity}
          onValueChange={onQuantityChange}
          formatOptions={QUANTITY_FORMAT}
        />
        <ConditionTextInput
          label="Price"
          value={price}
          onValueChange={onPriceChange}
          formatOptions={PRICE_FORMAT}
        />
        <DeliveryDatePicker
          deliveryDates={deliveryDates}
          selectedDate={deliveryDate}
          onSelect={onDateSelect}
        />
        <Flex>
          <Text fontSize="sm" color="text.secondary">
            You commit to supply {quantity || '—'} units at {price || '—'} cedis per unit
            {deliveryDate !== '' ? ` by ${formatDate(deliveryDate)}` : ''}.
          </Text>
        </Flex>
        {!hideSubmit && (
          <Button size="xs" borderRadius="lg" disabled={!isValid} onClick={handleSave}>
            {isEditing ? 'Update' : 'Commit'}
          </Button>
        )}
      </Flex>
    );
  },
);
CommitmentForm.displayName = 'CommitmentForm';

export { CommitmentForm, QUANTITY_FORMAT, PRICE_FORMAT };

// ─── Commitments List ────────────────────────────────────────────────────────

interface CommitmentsListProps {
  commitments: SupplyCommitment[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onAdd: () => void;
  onBack: () => void;
}

const CommitmentRow = memo(
  ({
    commitment,
    onEdit,
    onDelete,
  }: {
    commitment: SupplyCommitment;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
  }) => {
    return (
      <Flex py={2} alignItems="center" gap={3}>
        <Flex direction="column" gap={0.5} flex={1} minW={0}>
          <Text fontSize="xs" fontWeight="medium" truncate>
            {commitment.quantity} units @ GHS {commitment.price}
          </Text>
          <Text fontSize="xs" color="fg.subtle">
            by {formatDate(commitment.deliveryDate)}
          </Text>
        </Flex>
        <EditDeleteActions id={commitment.id} onEdit={onEdit} onDelete={onDelete} />
      </Flex>
    );
  },
);
CommitmentRow.displayName = 'CommitmentRow';

const CommitmentsList = memo(
  ({ commitments, onEdit, onDelete, onAdd, onBack }: CommitmentsListProps) => {
    return (
      <Flex direction="column" gap={3}>
        <Flex alignItems="center" gap={2}>
          <IconButton
            aria-label="Back"
            size="2xs"
            variant="ghost"
            borderRadius="md"
            onClick={onBack}
          >
            <LuArrowLeft />
          </IconButton>
          <Heading fontSize="sm" lineHeight={1} flex={1}>
            Your commitments
          </Heading>
          <Badge variant="outline" borderRadius="full" px={2}>
            {commitments.length}
          </Badge>
        </Flex>
        {commitments.length === 0 ? (
          <Text fontSize="xs" color="fg.subtle" py={2}>
            No commitments yet.
          </Text>
        ) : (
          <Flex direction="column" divideY="1px" divideColor="border.light">
            {commitments.map((c) => (
              <CommitmentRow
                key={c.id}
                commitment={c}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </Flex>
        )}
        <Button size="xs" borderRadius="lg" variant="outline" onClick={onAdd}>
          <LuPlus />
          Add commitment
        </Button>
      </Flex>
    );
  },
);
CommitmentsList.displayName = 'CommitmentsList';

// ─── Main Popover ────────────────────────────────────────────────────────────

interface SupplyCommitmentPopoverProps {
  needs: InventoryNeed[];
}

const SupplyCommitmentPopover = memo(({ needs }: SupplyCommitmentPopoverProps) => {
  const nextIdRef = useRef(1);
  const { open, onToggle, onClose, triggerRef, contentRef } = useControlledPopover<
    HTMLDivElement,
    HTMLButtonElement
  >({
    selector: '[role="listbox"]',
  });
  const [commitments, setCommitments] = useState<SupplyCommitment[]>([]);
  const [view, setView] = useState<PopoverView>('form');
  const [editingId, setEditingId] = useState<string | null>(null);

  const deliveryDates = useMemo(() => {
    const dates = new Set(needs.map((n) => n.deliveryBy));
    return Array.from(dates).sort();
  }, [needs]);

  const handleSave = useCallback(
    (values: Omit<SupplyCommitment, 'id'>) => {
      if (editingId != null) {
        setCommitments((prev) => prev.map((c) => (c.id === editingId ? { ...c, ...values } : c)));
        setEditingId(null);
        setView('list');
      } else {
        const newCommitment: SupplyCommitment = {
          id: `sc-${nextIdRef.current++}`,
          ...values,
        };
        setCommitments((prev) => [...prev, newCommitment]);
        setView('list');
      }
    },
    [editingId, nextIdRef],
  );

  const handleEdit = useCallback((id: string) => {
    setEditingId(id);
    setView('form');
  }, []);

  const handleDelete = useCallback((id: string) => {
    setCommitments((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const handleAdd = useCallback(() => {
    setEditingId(null);
    setView('form');
  }, []);

  const handleBackToList = useCallback(() => {
    setEditingId(null);
    setView('list');
  }, []);

  const handleBackToForm = useCallback(() => {
    setView('form');
    setEditingId(null);
  }, []);

  const editingCommitment =
    editingId != null ? commitments.find((c) => c.id === editingId) : undefined;

  const handleOpenChange = useCallback(() => {
    onToggle();
  }, [onToggle]);

  return (
    <Popover.Root
      open={open}
      onOpenChange={handleOpenChange}
      lazyMount
      positioning={{ placement: 'bottom-start' }}
    >
      <Popover.Trigger asChild>
        <Button ref={triggerRef} size="sm" borderRadius="lg" variant="surfaceOutline">
          <LuPackagePlus />
          Supply
          {commitments.length > 0 && (
            <Badge variant="solid" borderRadius="full" ml={1} size="xs">
              {commitments.length}
            </Badge>
          )}
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            ref={contentRef}
            borderRadius="xl"
            bgColor="surface.background"
            p={4}
            minW="280px"
            w="auto"
            maxW="340px"
          >
            {view === 'form' ? (
              <Flex direction="column" gap={3}>
                <CommitmentForm
                  deliveryDates={deliveryDates}
                  initialValues={editingCommitment}
                  onSave={handleSave}
                  onCancel={
                    commitments.length > 0 && editingId == null ? handleBackToList : undefined
                  }
                  isEditing={editingId != null}
                  key={editingId ?? 'new'}
                />
                {commitments.length > 0 && editingId == null && (
                  <Button
                    size="2xs"
                    variant="ghost"
                    borderRadius="md"
                    alignSelf="flex-start"
                    onClick={handleBackToList}
                  >
                    View commitments ({commitments.length})
                  </Button>
                )}
              </Flex>
            ) : (
              <CommitmentsList
                commitments={commitments}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAdd={handleAdd}
                onBack={handleBackToForm}
              />
            )}
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
});
SupplyCommitmentPopover.displayName = 'SupplyCommitmentPopover';

export default SupplyCommitmentPopover;
